"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.methodHandlerFactory = void 0;
const jwt_decode_1 = __importDefault(require("jwt-decode"));
let refreshTokenCallInProgress = null;
/**
 * Executes an RPC call.
 *
 * Authorization is handled by the `authHeaderProvider` from the options.
 *
 * @param options The options for the RPC call
 * @returns A promise containing the result of the RPC call
 */
const rpcCall = async (options) => {
    const { url, body, headers, authHeaderProvider } = options;
    const reqHeaders = {
        ...headers,
    };
    if (authHeaderProvider) {
        const authHeader = authHeaderProvider();
        if (authHeader) {
            reqHeaders.authorization = authHeader;
        }
    }
    const req = await fetch(url, {
        method: "POST",
        body,
        headers: reqHeaders,
    });
    if (req.status >= 400) {
        const errorMessage = await req.text();
        throw new Error(errorMessage);
    }
    const contentType = req.headers.get("content-type");
    if (contentType?.includes("application/json")) {
        const rpcResponse = await req.json();
        return rpcResponse.result;
    }
    else {
        const jsonError = {
            statusCode: 403,
            message: "403 or 404 API error suppressed by CF error pages setup",
        };
        throw new Error(JSON.stringify(jsonError));
    }
};
/**
 * Waits for the refresh token call to finish if it is in progress.
 *
 * Minimizes the number of refresh token calls that are made
 * Minimizes the number of 403 errors.
 *
 * After the refresh token call is finished, the original call is executed.
 *
 * `authTokenProvider` ensured `rpcCall` always has an up-to-date token.
 * @param call The RPC call to execute
 */
const waitIfRefreshTokenInProgress = (call) => {
    if (refreshTokenCallInProgress !== null) {
        refreshTokenCallInProgress.then(() => call);
    }
    return call;
};
/**
 * Executes a refreshTokens RPC call.
 *
 * Ensures that only 1 RPC call is running at any given time
 * */
const refreshTokensCall = (options) => {
    const { url, headers, tokensProvider, tokensRefreshedCallback, refreshTokenExpiredCallback } = options;
    if (refreshTokenCallInProgress) {
        return refreshTokenCallInProgress;
    }
    refreshTokenCallInProgress = rpcCall({
        url,
        headers,
        body: JSON.stringify({
            id: 0,
            service: "authentication",
            method: "refreshUserTokens",
            parameters: [{ refreshBoth: false }],
            // TODO: We are sticking the token in the header.
            // ? can we get rid of this?
            refresh_token: tokensProvider()?.refresh,
        }),
        authHeaderProvider() {
            // The refresh tokens is a special case, it needs to be authorized with the refresh token
            return `Bearer ${tokensProvider()?.refresh}`;
        },
    })
        .then((result) => {
        if (result.authentication) {
            tokensRefreshedCallback(result, false);
        }
        else {
            refreshTokenExpiredCallback();
        }
    })
        .catch((err) => {
        console.warn(err);
        refreshTokenExpiredCallback();
    })
        .finally(() => {
        refreshTokenCallInProgress = null;
    });
    return refreshTokenCallInProgress;
};
/**
 * A client-side toke expiration check.
 * @param token
 * @returns
 */
const isTokenExpired = (token) => {
    try {
        const decodedPayload = (0, jwt_decode_1.default)(token);
        const { exp } = decodedPayload;
        return exp < Date.now() / 1000;
    }
    catch (err) {
        console.error(err);
        return true;
    }
};
const methodHandlerFactory = (opts) => {
    const { rpcEndpoint: url, clientName, clientVersion, clientRevision, method, service, tokensProvider, tokensRefreshedCallback, refreshTokenExpiredCallback, } = opts;
    const correlationId = Math.random().toString(36).slice(-8);
    const headers = {
        "content-type": "application/json",
        "x-correlation-id": `rpc${correlationId}`,
        "x-daytrip-client": `${clientName ?? "RPC"}-rpc-sdk ${clientVersion}-${clientRevision}`,
    };
    /** Makes an RPC call, waits if there is a refresh token call in progress */
    const makeRpcCall = async (body) => waitIfRefreshTokenInProgress(rpcCall({
        url,
        body,
        headers,
        authHeaderProvider() {
            // The auth header is controlled by this method to ensure that the token is always up to date
            // If the token is refreshed before executing the actual RPC this will ensure it's up to date
            if (tokensProvider()?.authentication) {
                return `Bearer ${tokensProvider()?.authentication}`;
            }
            return null;
        },
    }));
    /** Checks if the token should be refreshed. */
    const shouldTokenBeRefreshed = () => {
        const token = tokensProvider()?.authentication;
        if (!token) {
            return false;
        }
        return isTokenExpired(token);
    };
    /** Syntactic sugar for refreshing tokens. */
    const refreshTokens = () => refreshTokensCall({
        url,
        headers,
        tokensProvider,
        tokensRefreshedCallback,
        refreshTokenExpiredCallback,
    });
    return async (...parameters) => {
        const body = JSON.stringify({
            id: 0,
            method,
            service,
            parameters,
        });
        if (shouldTokenBeRefreshed()) {
            await refreshTokens();
        }
        try {
            const result = await makeRpcCall(body);
            return result;
        }
        catch (error) {
            if (error !== "Tokens expired") {
                throw error;
            }
        }
        await refreshTokens();
        return makeRpcCall(body);
    };
};
exports.methodHandlerFactory = methodHandlerFactory;
//# sourceMappingURL=method-handler-factory.js.map